// src/components/user/UserDashboard.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, get, set, update } from 'firebase/database';
import { realtimeDb } from '../../firebase';
import { FaUserCircle, FaPlus, FaSignOutAlt, FaArrowRight } from 'react-icons/fa';
import DivisionCard from '../Admin/DivisionCard';

const UserDashboard = () => {
  const navigate = useNavigate();
  const [company, setCompany] = useState('');
  const [divisions, setDivisions] = useState([]);

  useEffect(() => {
    const storedCompany = localStorage.getItem('selectedCompany');
    console.log('✅ selectedCompany:', storedCompany);

    if (!storedCompany) {
      alert('No company assigned. Please log in again.');
      navigate('/');
      return;
    }

    setCompany(storedCompany);

    const defaultDivisions = [];

    const fetchDivisions = async () => {
      const companyRef = ref(realtimeDb, `companies/${storedCompany}`);
      const snapshot = await get(companyRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        const savedDivisions = data.divisions || [];
        const combined = Array.from(new Set([...defaultDivisions, ...savedDivisions]));
        setDivisions(combined);
        await update(companyRef, { divisions: combined });
      } else {
        await set(companyRef, {
          divisions: defaultDivisions,
          kvaPrices: {},
        });
        setDivisions(defaultDivisions);
      }
    };

    fetchDivisions();
  }, [navigate]);

  const handleAddDivision = async () => {
    const newDivision = prompt('Enter new division name:');
    if (newDivision && !divisions.includes(newDivision)) {
      const updated = [...divisions, newDivision];
      setDivisions(updated);
      const companyRef = ref(realtimeDb, `companies/${company}`);
      await update(companyRef, { divisions: updated });
    }
  };

  const handleDivisionClick = (division) => {
    navigate(`/user/division/${division}`);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
              {company} 
            </h1>
            <p className="text-gray-600">User Dashboard</p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/user/profile')}
              className="flex items-center gap-2 bg-white hover:bg-gray-100 text-gray-800 font-semibold py-2 px-4 border border-gray-300 rounded-lg shadow-sm"
            >
              <FaUserCircle className="text-blue-600" />
              <span>Profile</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <FaSignOutAlt />
              <span>Logout</span>
            </button>
          </div>
        </div>

        {company ? (
          <div className="bg-white rounded-xl shadow-md overflow-hidden p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-full bg-indigo-100 text-indigo-600">
                <FaArrowRight size={20} />
              </div>
              <h2 className="text-xl font-semibold text-gray-800">Select Division</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {divisions.map((div) => (
                <DivisionCard
                  key={div}
                  name={div}
                  onClick={() => handleDivisionClick(div)}
                  showArrow={true}
                />
              ))}

              <button
                onClick={handleAddDivision}
                className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 hover:border-indigo-500 rounded-xl p-6 transition-all hover:bg-indigo-50 group"
              >
                <div className="p-3 rounded-full bg-gray-100 group-hover:bg-indigo-100 mb-2 transition-colors">
                  <FaPlus className="text-gray-500 group-hover:text-indigo-600 transition-colors" />
                </div>
                <span className="text-gray-600 group-hover:text-indigo-600 font-medium transition-colors">
                  Add New Division
                </span>
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <div className="text-gray-400 mb-4">
              <FaUserCircle className="mx-auto text-5xl" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Company Assigned</h3>
            <p className="text-gray-500">Please contact your administrator</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDashboard;
